import state from '@ugc-store';
import DOMPurify from 'isomorphic-dompurify';
import { HttpStatus } from './http-status';

export const isRtl = () => document.dir === 'rtl';
export const detectIsRTL = (language: string): boolean => language === 'ar-SA' || language === 'he-IL';

export const MIN_CHARACTERS = 5;
export const MAX_CHARACTERS_200 = 200;
export const MAX_CHARACTERS = 500;
export const MAX_ANSWER_CHARACTERS = 2000;

export const stringFormat = (formatString, ...args) => {
  if (!formatString) return '';
  return formatString.replace(/\{(\d+)\}/g, (_match, index) => {
    return args[index];
  });
};

export const getCookie = cname => {
  let name = cname + '=';
  let ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
  }

  return '';
};

export const getUrlParams = paramStr => {
  if (paramStr.length === '' || typeof paramStr !== 'string') return { aid: '' };

  let vars = paramStr.split('&');
  const paramsObj = {};
  for (let i = 0; i < vars.length; i++) {
    let [objName, objValue] = vars[i].split('=');
    paramsObj[objName] = objValue;
  }

  return paramsObj;
};

export const getLanguageCode = () => {
  return state.preference.language;
};

export const debounce = (fn, delay, _this = null): any => {
  let timer = null;
  return function () {
    if (timer) {
      clearTimeout(timer);
      timer = setTimeout(_this ? fn.bind(_this) : fn, delay, _this);
    } else {
      timer = setTimeout(_this ? fn.bind(_this) : fn, delay, _this);
    }
  };
};

export const throttle = (callback, delay) => {
  let throttleTimeout = null;
  let storedEvent = null;

  const throttledEventHandler = event => {
    storedEvent = event;

    const shouldHandleEvent = !throttleTimeout;

    if (shouldHandleEvent) {
      callback(storedEvent);

      storedEvent = null;

      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;

        if (storedEvent) {
          throttledEventHandler(storedEvent);
        }
      }, delay);
    }
  };

  return throttledEventHandler;
};

export const isProd = () => window.location.host.includes('iherb.com');

export const isPreprod = () => window.location.host.includes('iherbpreprod.com');

export const isTest = () => window.location.host.includes('iherbtest.com');

const getUrl = () => (isProd() ? 'iherb' : isPreprod() ? 'iherbpreprod' : 'iherbtest');

export const getLoginUrl = (): string =>
  `//checkout.${getUrl()}.com/account/login?referrerRedirect=true&returnUrl=${encodeURIComponent(
    window.location.href
  )}`;

export const getRegisterUrl = (): string => `//checkout.${getUrl()}.com/auth/Account/Register`;

export const login = () => {
  window.location.href = getLoginUrl();
};

export const register = () => {
  window.location.href = getRegisterUrl();
};

export const messageMatch = (status = -1, message = '', data = {}) => {
  if (message !== '') {
    return { message, apiError: true };
  }

  if (HttpStatus.UNAUTHORIZED === status) {
    login();
    return;
  }

  const badRequest = message || 'IDS_UGC_UNEXPECTED_ERROR';
  const errorMap = new Map();
  errorMap.set(HttpStatus.INTERNAL_SERVER_ERROR, 'IDS_UGC_SERVICE_DOWN');
  errorMap.set(HttpStatus.BAD_REQUEST, badRequest);
  errorMap.set(HttpStatus.NOT_FOUND, 'IDS_UGC_NOT_FOUND');

  return {
    message: errorMap.get(status),
    apiError: false,
    status,
    data,
  };
};

const getDefaultErrorMessage = data => {
  return data.IDS_UGC_UNEXPECTED_ERROR || 'Unexpected error occurred. Contact support team if the issue persists.';
};

export const handleErrorMessage = error => {
  if (!error) return;

  const { message, apiError } = error;
  const { qnaStaticLanguage } = state;

  if (apiError) {
    showToast({
      type: 'warn',
      message: [message],
    });
  } else {
    showToast({
      type: 'error',
      message: [qnaStaticLanguage[message] || getDefaultErrorMessage(qnaStaticLanguage)],
    });
  }
};

export const getRewardsCode = () => {
  if (window) {
    const { shareWidgetContent } = window as any;
    if (shareWidgetContent && shareWidgetContent.rewardsCode && !shareWidgetContent.rewardsCode.includes(' ')) {
      return shareWidgetContent.rewardsCode || '';
    } else {
      return '';
    }
  } else {
    return '';
  }
};

export const getArray = data => (Array.isArray(data) ? data : []);

export const detectIsMobile = (): boolean =>
  /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
    window.navigator.userAgent
  ) ||
  /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| ||a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
    window.navigator.userAgent.substr(0, 4)
  );

export const getLocaleDateString = (postedDate, languageCode) =>
  new Date(postedDate).toLocaleDateString(languageCode, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const imageSizes = {
  50: 'b',
  75: 's',
  100: 't',
  120: 'k',
  160: 'c',
  200: 'm',
  240: 'r',
  300: 'w',
  320: 'u',
  400: 'g',
  600: 'v',
  800: 'y',
  1600: 'l',
};

export const createProductImageUrl = ({ partNumber, size, index, host }) => {
  const hasHyphen = partNumber.indexOf('-') > -1;
  const partNum = partNumber.toLowerCase();

  if (hasHyphen) {
    const b = partNum.replace('-', '');
    const [p] = partNum.split('-');

    return `${host}/${p}/${b}/${imageSizes[size]}/${index}.jpg`;
  }
  const p = partNum.slice(0, 3);
  return `${host}/${p}/${partNum}/${imageSizes[size]}/${index}.jpg`;
};

export const getUrlNameFromRoute = () => {
  return getArray(window.location.pathname.split('/'))[2];
};
export const getProductIdFromRoute = () => {
  return getArray(window.location.pathname.split('/'))[3];
};

export const showToast = async info => {
  await customElements.whenDefined('ugc-alert');
  const alert = document.querySelector('ugc-alert');
  await alert.showToast(info);
  return Promise.resolve();
};

export const unique = arr => {
  const res = [];
  for (let i = 0; i < arr.length; i++) {
    if (res.indexOf(arr[i]) == -1) {
      res.push(arr[i]);
    }
  }
  return res;
};

export const toParse = data => {
  try {
    if (data) {
      return JSON.parse(data);
    }
    return {};
  } catch (e) {
    return {};
  }
};

export const toProfileImageUrl = profileInfo => {
  let profileImageUrl = '';
  if (profileInfo.image && profileInfo.image.thumbnails) {
    const thumbnails = profileInfo.image.thumbnails;
    profileImageUrl = (Array.isArray(thumbnails) && thumbnails.length > 0 && thumbnails[0].fullPath) || '';
  }
  return profileImageUrl;
};

declare global {
  interface Window {
    msCrypto: any;
    productCardHtmlInReview: string;
  }
}
export const getRandom = () => {
  const crypto = window.crypto || window.msCrypto;
  var array = new Uint32Array(1);
  return crypto.getRandomValues(array)[0];
};

export const addDataLayer = eventData => {
  const dataLayer = window.dataLayer || [];
  dataLayer.push(eventData);
  window.dataLayer = dataLayer;
};

export enum FontType {
  small,
  large,
}

export enum PageType {
  qna = 1,
  review,
}
export const toLocalSeparator = (count: number) =>
  new Intl.NumberFormat(`${state.preference.language}-u-nu-latn`).format(count);

export const generatePriceGA = (price: string): number => Number(price.replace(/([^0-9\.]*)/g, ''));

export const isPercent = (value: string) => {
  try {
    return /^\d+\.?\d{0,2}%$/.test(value);
  } catch (err) {
    return false;
  }
};

export const hasHtmlTag = (content: string) => {
  try {
    if (!content) return false;
    return /^([\s\S]*<[a-z][^>]*>)[\s\S]*$/i.test(content);
  } catch (err) {
    return false;
  }
};

export const xssFilter = (content: string): string => DOMPurify.sanitize(content, { ALLOWED_TAGS: ['mark'] });
